import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import config from '../../config/environment';

const API_BASE_URL = config.apiBaseUrl;

export default function InventoryByLocation() {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('sku');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedSKU, setSelectedSKU] = useState(null);

  // Fetch inventory data
  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/inventory/locations`,
        {
          headers: { 'X-Tenant-ID': 'tenant-123' },
          params: { search: searchTerm }
        }
      );
      setInventory(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, [searchTerm]);

  // Sort inventory
  const sortedInventory = [...inventory].sort((a, b) => {
    let aVal, bVal;

    if (sortField === 'total_qty') {
      aVal = a.total_qty;
      bVal = b.total_qty;
    } else if (sortField.startsWith('locations.')) {
      const location = sortField.split('.')[1];
      aVal = a.locations[location];
      bVal = b.locations[location];
    } else {
      aVal = a[sortField];
      bVal = b[sortField];
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddStock = (sku) => {
    setSelectedSKU(sku);
    setShowAddStockModal(true);
  };

  const handleTransfer = (sku) => {
    setSelectedSKU(sku);
    setShowTransferModal(true);
  };

  const handleViewTransactions = (sku) => {
    setSelectedSKU(sku);
    setShowTransactionsModal(true);
  };

  return (
    <div className="p-3">
      {/* Header - Compact */}
      <div className="mb-3">
        <h1 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
          Inventory by Location
        </h1>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Track inventory quantities across GAR, FBA, AWD, and OFF locations
        </p>
      </div>

      {/* Controls - Compact */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search by SKU or product title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Add Stock Button */}
        <button
          onClick={() => setShowAddStockModal(true)}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
        >
          + Add Stock
        </button>

        {/* Refresh Button */}
        <button
          onClick={fetchInventory}
          className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-lg transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Table - Compact */}
      <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-4 text-center text-xs text-gray-500 dark:text-gray-400">
            Loading inventory...
          </div>
        ) : sortedInventory.length === 0 ? (
          <div className="p-4 text-center text-xs text-gray-500 dark:text-gray-400">
            No inventory found. Add stock to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th
                    onClick={() => handleSort('sku')}
                    className="px-3 py-2 text-left text-[10px] uppercase font-semibold text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    SKU {sortField === 'sku' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('product_title')}
                    className="px-3 py-2 text-left text-[10px] uppercase font-semibold text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Product {sortField === 'product_title' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('locations.GAR')}
                    className="px-3 py-2 text-center text-[10px] uppercase font-semibold text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    GAR {sortField === 'locations.GAR' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('locations.FBA')}
                    className="px-3 py-2 text-center text-[10px] uppercase font-semibold text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    FBA {sortField === 'locations.FBA' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('locations.AWD')}
                    className="px-3 py-2 text-center text-[10px] uppercase font-semibold text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    AWD {sortField === 'locations.AWD' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('locations.OFF')}
                    className="px-3 py-2 text-center text-[10px] uppercase font-semibold text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    OFF {sortField === 'locations.OFF' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('total_qty')}
                    className="px-3 py-2 text-center text-[10px] uppercase font-semibold text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Total {sortField === 'total_qty' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase font-semibold text-gray-700 dark:text-gray-200">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortedInventory.map((item) => (
                  <tr
                    key={item.sku}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-white">
                      {item.sku}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 truncate max-w-[150px]" title={item.product_title}>
                      {item.product_title?.substring(0, 25)}{item.product_title?.length > 25 ? '...' : ''}
                    </td>
                    <td className={`px-3 py-2 text-xs text-center font-medium ${
                      Math.max(0, item.locations.GAR) < 10 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                    }`}>
                      {Math.max(0, item.locations.GAR)}
                    </td>
                    <td className={`px-3 py-2 text-xs text-center font-medium ${
                      Math.max(0, item.locations.FBA) < 10 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                    }`}>
                      {Math.max(0, item.locations.FBA)}
                    </td>
                    <td className={`px-3 py-2 text-xs text-center font-medium ${
                      Math.max(0, item.locations.AWD) < 10 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                    }`}>
                      {Math.max(0, item.locations.AWD)}
                    </td>
                    <td className={`px-3 py-2 text-xs text-center font-medium ${
                      Math.max(0, item.locations.OFF) < 10 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                    }`}>
                      {Math.max(0, item.locations.OFF)}
                    </td>
                    <td className="px-3 py-2 text-xs text-center font-bold text-gray-900 dark:text-white">
                      {Math.max(0, item.total_qty)}
                    </td>
                    <td className="px-3 py-2 text-xs text-center">
                      <div className="flex gap-1 justify-center">
                        <button
                          onClick={() => handleAddStock(item.sku)}
                          className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-[10px] rounded transition-colors"
                          title="Add Stock"
                        >
                          +
                        </button>
                        <button
                          onClick={() => handleTransfer(item.sku)}
                          className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-[10px] rounded transition-colors"
                          title="Transfer"
                        >
                          ⇄
                        </button>
                        <button
                          onClick={() => handleViewTransactions(item.sku)}
                          className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white text-[10px] rounded transition-colors"
                          title="History"
                        >
                          📋
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddStockModal && (
        <AddStockModal
          sku={selectedSKU}
          onClose={() => {
            setShowAddStockModal(false);
            setSelectedSKU(null);
            fetchInventory();
          }}
        />
      )}

      {showTransferModal && (
        <TransferStockModal
          sku={selectedSKU}
          inventory={inventory.find((i) => i.sku === selectedSKU)}
          onClose={() => {
            setShowTransferModal(false);
            setSelectedSKU(null);
            fetchInventory();
          }}
        />
      )}

      {showTransactionsModal && (
        <TransactionHistoryModal
          sku={selectedSKU}
          onClose={() => {
            setShowTransactionsModal(false);
            setSelectedSKU(null);
          }}
        />
      )}
    </div>
  );
}

// Add Stock Modal Component
function AddStockModal({ sku, onClose }) {
  const [formData, setFormData] = useState({
    sku: sku || '',
    location: 'GAR',
    qty: '',
    reference: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    // Fetch products for SKU dropdown
    const fetchProducts = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/inventory/locations`,
          { headers: { 'X-Tenant-ID': 'tenant-123' } }
        );
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}/inventory/stock`,
        {
          ...formData,
          qty: parseInt(formData.qty)
        },
        { headers: { 'X-Tenant-ID': 'tenant-123' } }
      );
      toast.success('✅ Stock added successfully!');
      onClose();
    } catch (error) {
      console.error('Error adding stock:', error);
      toast.error('❌ Failed to add stock: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          ➕ Add Stock
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* SKU Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                SKU
              </label>
              <select
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select SKU</option>
                {products.map((p) => (
                  <option key={p.sku} value={p.sku}>
                    {p.sku} - {p.product_title}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location
              </label>
              <select
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="GAR">🏠 GAR - Garage (Warehouse)</option>
                <option value="FBA">📦 FBA - Amazon FBA</option>
                <option value="AWD">🚚 AWD - Amazon AWD</option>
                <option value="OFF">🏢 OFF - Offsource (3PL)</option>
              </select>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quantity
              </label>
              <input
                type="number"
                value={formData.qty}
                onChange={(e) => setFormData({ ...formData, qty: e.target.value })}
                required
                min="1"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Reference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reference (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g., PO-2025-001"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Stock'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Transfer Stock Modal Component
function TransferStockModal({ sku, inventory, onClose }) {
  const [formData, setFormData] = useState({
    sku: sku || '',
    from_location: 'GAR',
    to_location: 'FBA',
    qty: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const availableQty = inventory?.locations[formData.from_location] || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}/inventory/transfer`,
        {
          ...formData,
          qty: parseInt(formData.qty)
        },
        { headers: { 'X-Tenant-ID': 'tenant-123' } }
      );
      toast.success('✅ Stock transferred successfully!');
      onClose();
    } catch (error) {
      console.error('Error transferring stock:', error);
      toast.error('❌ Failed to transfer stock: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          🔄 Transfer Stock
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* SKU (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                SKU
              </label>
              <input
                type="text"
                value={formData.sku}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* From Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                From Location
              </label>
              <select
                value={formData.from_location}
                onChange={(e) => setFormData({ ...formData, from_location: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="GAR">🏠 GAR ({inventory?.locations.GAR || 0} available)</option>
                <option value="FBA">📦 FBA ({inventory?.locations.FBA || 0} available)</option>
                <option value="AWD">🚚 AWD ({inventory?.locations.AWD || 0} available)</option>
                <option value="OFF">🏢 OFF ({inventory?.locations.OFF || 0} available)</option>
              </select>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Available: {availableQty} units
              </p>
            </div>

            {/* To Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                To Location
              </label>
              <select
                value={formData.to_location}
                onChange={(e) => setFormData({ ...formData, to_location: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="GAR">🏠 GAR - Garage</option>
                <option value="FBA">📦 FBA - Amazon FBA</option>
                <option value="AWD">🚚 AWD - Amazon AWD</option>
                <option value="OFF">🏢 OFF - Offsource</option>
              </select>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quantity
              </label>
              <input
                type="number"
                value={formData.qty}
                onChange={(e) => setFormData({ ...formData, qty: e.target.value })}
                required
                min="1"
                max={availableQty}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Transferring...' : 'Transfer Stock'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Transaction History Modal Component
function TransactionHistoryModal({ sku, onClose }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/inventory/transactions`,
          {
            headers: { 'X-Tenant-ID': 'tenant-123' },
            params: { sku, page_size: 100 }
          }
        );
        setTransactions(response.data);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [sku]);

  const getTypeColor = (type) => {
    switch (type) {
      case 'PURCHASE':
        return 'text-green-600 dark:text-green-400';
      case 'SALE':
        return 'text-red-600 dark:text-red-400';
      case 'TRANSFER':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          📜 Transaction History - {sku}
        </h2>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <p className="text-center text-gray-500 dark:text-gray-400">Loading...</p>
          ) : transactions.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400">No transactions found</p>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Date</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Type</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">From</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">To</th>
                  <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Qty</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Reference</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {transactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                      {new Date(txn.date).toLocaleDateString()}
                    </td>
                    <td className={`px-4 py-2 text-sm font-medium ${getTypeColor(txn.type)}`}>
                      {txn.type}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {txn.from_location || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {txn.to_location || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-right font-medium text-gray-900 dark:text-white">
                      {txn.qty}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {txn.reference || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {txn.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
